import { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Paper,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import { z } from 'zod';
import axiosInstance from '../services/axiosInstance';
import SuccessPopup from './SuccessPopup';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';

// IMPORTANT: Make sure to place your image in the correct folder
// and import it here.
import BackgroundBanner from '../assets/image/Background Banner-02.png';

// --- 1. Define Zod Schema ---
const enquirySchema = z.object({
  firstName: z.string().min(1, "First Name is required"),
  lastName: z.string().min(1, "Last Name is required"),
  mobile: z.string()
    .min(8, "Mobile number must be at least 8 digits")
    .regex(/^[0-9]+$/, "Digits only"),
  email: z.string().email("Invalid email address"),
  enquiryType: z.string().min(1, "Please select an enquiry type"),
  message: z.string().optional(),
});

type EnquiryFormData = z.infer<typeof enquirySchema>;

const EnquirySection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- State ---
  const [formData, setFormData] = useState<EnquiryFormData>({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    enquiryType: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof EnquiryFormData, string>>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  // --- Handlers ---
  const handleChange = (field: keyof EnquiryFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEnquiry = async () => {
    const validationResult = enquirySchema.safeParse(formData);

    if (!validationResult.success) {
      const fieldErrors: any = {};
      validationResult.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0]] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);

    try {
      const fullName = `${formData.firstName} ${formData.lastName}`;

      const response = await axiosInstance.post(
        "/CustomerVehicle/CustomerEnquiryCreate",
        {
          customerName: fullName,
          mobileNo: formData.mobile,
          email: formData.email,
          enquiryType: formData.enquiryType,
          remarks: formData.message
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data as {
        responseCode: string;
        responseMessage?: string;
      };

      if (result.responseCode === "00") {
        setShowSuccess(true);
        setFormData({
          firstName: '', lastName: '', mobile: '', email: '', enquiryType: '', message: ''
        });
      } else {
        console.error("API Error:", result.responseMessage);
      }

    } catch (error) {
      console.error("❌ Error submitting enquiry", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>

      <Typography
        variant="h3"
        align="center"
        fontWeight={600}
        sx={{
          color: "#ffaa00",
          mb: 6,
          letterSpacing: '-1px'
        }}
      >
        Get In Touch
      </Typography>

      <Paper
        elevation={0}
        sx={{
          maxWidth: 1200,
          mx: "auto",
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1)',
          bgcolor: '#fff'
        }}
      >
        <Grid container>

          {/* LEFT: IMAGE & INFO */}
          <Grid size={{ xs: 12, md: 5 }} sx={{ position: 'relative' }}>
            <Box
              sx={{
                height: '100%',
                minHeight: { xs: 450, md: 'auto' },
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative'
              }}
            >
              {/* Overlay */}
              <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#FFC933', p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center', color: '#fff' }}>
                <Fade in={true} timeout={1000}>
                  <Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ lineHeight: 1.3 }}>
                      We’re Here to Support Your Commercial Vehicle Needs
                    </Typography>

                    <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6, mb: 4, mt: 2 }}>
                      Whether you’re looking to buy, sell, or list commercial vehicles, or want clearer insight into the market, WorkHorse is here to help.
                      <br /><br />
                      We connect businesses, fleet owners, and dealers with reliable commercial vehicle solutions, backed by real market data and practical insights.
                    </Typography>

                    <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(255,255,255,0.2)', pt: 3 }}>
                      <Stack spacing={2}>
                        {/* Location - Static (Kept White) */}
                        <Box display="flex" alignItems="center" gap={2}>
                          <LocationOnIcon sx={{ color: '#fff' }} />
                          <Typography variant="body2" fontWeight={600}>Singapore</Typography>
                        </Box>

                        {/* Phone - Click to Call */}
                        <Box
                          component="a"
                          href="tel:+6588413368"
                          display="flex"
                          alignItems="center"
                          gap={2}
                          sx={{
                            color: 'inherit', // Starts white (inherited from parent)
                            textDecoration: 'none',
                            transition: '0.3s', // Makes the color change smooth
                            '&:hover': {
                              color: '#000' // <--- TURNS TEXT & ICON BLACK
                            }
                          }}
                        >
                          {/* Changed color to 'inherit' so it turns black on hover */}
                          <PhoneIcon sx={{ color: 'inherit' }} />
                          <Typography variant="body2" fontWeight={600}>88413368</Typography>
                        </Box>

                        {/* Email - Click to Email */}
                        <Box
                          component="a"
                          href="https://mail.google.com/mail/?view=cm&to=hello@workhorse.com.sg"
                          target="_blank"
                          rel="noopener noreferrer"
                          display="flex"
                          alignItems="center"
                          gap={2}
                          sx={{
                            color: 'inherit', // Starts white
                            textDecoration: 'none',
                            transition: '0.3s', // Makes the color change smooth
                            '&:hover': {
                              color: '#000' // <--- TURNS TEXT & ICON BLACK
                            }
                          }}
                        >
                          {/* Changed color to 'inherit' so it turns black on hover */}
                          <EmailIcon sx={{ color: 'inherit' }} />
                          <Typography variant="body2" fontWeight={600}>hello@workhorse.com.sg</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                </Fade>
              </Box>
            </Box>
          </Grid>

          {/* RIGHT: FORM */}
          <Grid size={{ xs: 12, md: 7 }} >
            <Box sx={{ p: { xs: 3, md: 6 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Slide direction="up" in={true} mountOnEnter unmountOnExit>
                <Box>
                  {/* Form Intro Text */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" fontWeight={800} sx={{ color: '#333' }}>
                      We’re Here to Help
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                      Have questions about commercial vehicles, dealer listings, or market insights? Leave us a message — our team will get back to you shortly.
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    {/* Name Row */}
                    <Grid size={{ xs: 12, sm: 6 }} >
                      <TextField
                        fullWidth label="First Name*" variant="outlined"
                        value={formData.firstName}
                        onChange={(e) => handleChange('firstName', e.target.value)}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} >
                      <TextField
                        fullWidth label="Last Name*" variant="outlined"
                        value={formData.lastName}
                        onChange={(e) => handleChange('lastName', e.target.value)}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>

                    {/* Contact Row */}
                    <Grid size={{ xs: 12, sm: 6 }} >
                      <TextField
                        fullWidth label="Phone Number*" variant="outlined"
                        value={formData.mobile}
                        onChange={(e) => handleChange('mobile', e.target.value)}
                        error={!!errors.mobile}
                        helperText={errors.mobile}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} >
                      <TextField
                        fullWidth label="Email*" variant="outlined"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        error={!!errors.email}
                        helperText={errors.email}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>

                    {/* Enquiry Type - UPDATED LIST */}
                    <Grid size={{ xs: 12 }} >
                      <TextField
                        select fullWidth label="Select Your Enquiry Type" variant="outlined"
                        value={formData.enquiryType}
                        onChange={(e) => handleChange('enquiryType', e.target.value)}
                        error={!!errors.enquiryType}
                        helperText={errors.enquiryType}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      >
                        <MenuItem value="Buying a Commercial Vehicle">Buying a Commercial Vehicle</MenuItem>
                        <MenuItem value="Selling a Commercial Vehicle">Selling a Commercial Vehicle</MenuItem>
                        <MenuItem value="Dealer Registration">Dealer Registration</MenuItem>
                        <MenuItem value="Fleet / Bulk Enquiry">Fleet / Bulk Enquiry</MenuItem>
                        <MenuItem value="Market Insights & Pricing">Market Insights & Pricing</MenuItem>
                        <MenuItem value="General Enquiry">General Enquiry</MenuItem>
                      </TextField>
                    </Grid>

                    {/* Message */}
                    <Grid size={{ xs: 12 }} >
                      <TextField
                        fullWidth label="Your Message" variant="outlined" multiline rows={4}
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>

                    {/* Submit Button */}
                    <Grid size={{ xs: 12 }} >
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleEnquiry}
                        disabled={loading}
                        endIcon={!loading && <SendIcon />}
                        sx={{
                          py: 1.8,
                          borderRadius: '50px',
                          fontWeight: 600,
                          fontSize: '1rem',
                          textTransform: 'uppercase',
                          bgcolor: '#FFB800',
                          color: '#000',
                          boxShadow: '0 8px 20px -4px rgba(255, 184, 0, 0.4)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: '#FFC933',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 25px -4px rgba(255, 184, 0, 0.5)',
                          }
                        }}
                      >
                        {loading ? <CircularProgress size={12} color="inherit" /> : "SUBMIT ENQUIRY"}
                      </Button>
                    </Grid>

                  </Grid>
                </Box>
              </Slide>
            </Box>
          </Grid>

        </Grid>
      </Paper>

      <SuccessPopup
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
    </>
  );
};

export default EnquirySection;